import React, { useState, useEffect } from 'react';
import NavBar from 'components/NavBar.jsx';
import GeckoLocation from 'services/gecko-location';
import GeckoStore from 'services/gecko-store';
import CloneBotModal from '../CloneBotModal';
import TalkToSupportModal from '../TalksToSupportModal';
import { GeckoModalService } from 'ui';
import ActiveBotHead from './ActiveBotHead';
import ActiveBotBody from './ActiveBotBody';
import BotStats from './BotStats';
import Loader from 'components/Loader';
import { useBot, useBots } from '../hooks';
import './index.scss';
import BotAnswersImportService from '../Answers/Imports/BotAnswersImportReview/Service';
import GeckoPermissions from 'services/gecko-permissions';

function BotChatHead({ bot, active, onClick }) {
  return (
    <div className={`panel ${active}`} onClick={onClick} role="button">
      {bot.name}
    </div>
  );
}

function BotSelector({ value, onChange }) {
  const [open, setOpen] = useState(false);
  const [bots] = useBots();
  if (bots.length < 2) return null;
  return (
    <div className="BotSelector">
      <button
        className={`accordion ${open ? 'active-accordion' : ''}`}
        onClick={() => setOpen(!open)}
      >
        Change Bot
      </button>
      <div className={`${open ? 'open-accordion' : 'close-accordion'}`}>
        {bots.map((bot) => {
          return (
            <BotChatHead
              key={bot.id}
              active={value === bot.id}
              bot={bot}
              onClick={() => onChange(bot.id)}
            />
          );
        })}
      </div>
    </div>
  );
}

function BotsContainer({ botId, className, subsection, children }) {
  const [activeBot, isLoading] = useBot(botId);
  const navLinks =
    botId !== 'new'
      ? [
          {
            to: `/bots/${botId}/answers`,
            activeClassName: 'active',
            linkText: 'Answers',
          },
          {
            to: `/bots/${botId}/skills`,
            activeClassName: 'active',
            linkText: 'Skills',
          },
          {
            to: `/bots/${botId}/train`,
            activeClassName: 'active',
            linkText: 'Train',
          },
        ]
      : [];

  useEffect(() => {
    if (botId) {
      GeckoStore.localStorage.set('last-opened-bot', botId);
    }
  }, [botId]);

  const addNewBot = () => {
    return GeckoLocation.goTo('/bots/new/settings');
  };

  const cloneBot = () => {
    return new GeckoModalService().open(() => <CloneBotModal />);
  };

  const askSupport = () => {
    return new GeckoModalService().open(() => <TalkToSupportModal />);
  };

  const openBot = (id) => {
    const [_, _botsPart, _botId, route, _routeId] =
      GeckoLocation.location.pathname.split('/');
    // Redirect to answers page (when reviewing an import)
    if (route.includes('answers-import-review')) {
      return GeckoLocation.goTo(`/bots/${id}/answers`);
    }
    return GeckoLocation.goTo(`/bots/${id}/${route}`);
  };

  const items = [
    {
      type: 'default',
      name: 'Import Answers',
      icon: 'far fa-upload',
      inactive: subsection === 'answers',
      handler: () => BotAnswersImportService.openUploadModal(botId),
    },
    {
      type: 'primary',
      name: 'Create Answer',
      icon: 'far fa-plus',
      inactive: subsection === 'answers',
      preset: 'save',
      handler: () => {
        GeckoLocation.goTo(`/bots/${botId}/answers/new`);
      },
    },
    {
      type: 'primary',
      name: 'Create Skill',
      icon: 'far fa-plus',
      inactive: subsection === 'skills',
      preset: 'save',
      handler: () => GeckoLocation.goTo(`/bots/${botId}/skills/new`),
    },
  ];

  const isNewBot = botId === 'new';
  const isSupport = GeckoStore.redux.get('appState.isSupport');

  return (
    <div>
      <NavBar navLinks={navLinks} noSearch items={items}>
        <React.Fragment>
          {isSupport ? (
            <div className="dropdown gecko-dropdown">
              <span
                className="BotsContainer-NewButton"
                id="navbarDropdownMenuLink"
                data-toggle="dropdown"
                aria-haspopup="true"
                aria-expanded="false"
                role="button"
              >
                <i className="far fa-plus"></i> Create a Bot
              </span>
              <div
                className="BotsContainer-NewButton-dropdown gecko-dropdown-menu dropdown-menu dropdown-menu-left"
                aria-labelledby="navbarDropdownMenuLink"
                x-placement="bottom-start"
              >
                <span
                  className="BotsContainer-NewButton-new dropdown-item"
                  onClick={addNewBot}
                  role="button"
                >
                  <i className="far fa-plus"></i> Create a New Bot
                </span>
                <span
                  className="BotsContainer-NewButton-clone dropdown-item"
                  onClick={cloneBot}
                >
                  <i className="far fa-clone"></i> Clone an Existing Bot
                </span>
              </div>
            </div>
          ) : (
            <div className="dropdown gecko-dropdown">
              <span
                className="BotsContainer-NewButton"
                id="navbarDropdownMenuLink"
                data-toggle="dropdown"
                aria-expanded="false"
                role="button"
                onClick={askSupport}
              >
                <i className="far fa-plus"></i> Create a Bot
              </span>
            </div>
          )}

          <span className="nav-bar-breaker"></span>
        </React.Fragment>
      </NavBar>
      <Loader isLoading={isLoading} spinnerPageCenter>
        <div className="content-margin">
          <div className={`BotsContainer row ${className || ''}`}>
            <div className="BotsContainer-Bot col">
              <div className="active-bot">
                <ActiveBotHead
                  key={`${botId}-head`}
                  bot={activeBot}
                  botId={botId}
                />
                <ActiveBotBody
                  key={`${botId}-body`}
                  bot={activeBot}
                  botId={botId}
                />
              </div>

              {!isNewBot ? (
                <BotStats
                  key={`${botId}-stats`}
                  bot={activeBot}
                  botId={botId}
                />
              ) : null}

              <BotSelector value={botId} onChange={openBot} />
            </div>

            <div className="BotsContainer-TabView col">
              <div className="BotsContainer-Content" key={botId}>
                {children}
              </div>
            </div>
          </div>
        </div>
      </Loader>
    </div>
  );
}

export default BotsContainer;
